// src/icons/chains/ArbitrumLogo/ArbitrumLogo.react.tsx
import React from "react";

// src/styles/utils/fill.ts
import { tv as tv2 } from "tailwind-variants";

// src/utils/object.ts
var keys = (obj) => Object.keys(obj);
var fromEntries = (entries) => Object.fromEntries(entries);

// src/styles/tailwindBase.ts
import aspectRatio from "@tailwindcss/aspect-ratio";
import animate from "tailwindcss-animate";

// src/constants/breakpoints.ts
var breakpoints = {
  xs: 0,
  sm: 393,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
  "3xl": 1728,
  "4xl": 1920,
  max: 2560
};

// src/constants/zIndex.ts
var current = 0;
var get = () => {
  current += 100;
  return current.toString();
};
var Z_INDEX = {
  sidebar: get(),
  overlay: get(),
  sheet: get(),
  modal: get(),
  tooltip: get(),
  toast: get()
};

// src/styles/tailwindBase.ts
var numberToPx = (n) => {
  return `${n}px`;
};
var COMMON_COLORS = {
  // General utility
  transparent: "transparent",
  current: "currentColor",
  inherit: "inherit",
  white: "rgb(var(--color-white) / <alpha-value>)",
  black: "rgb(var(--color-black) / <alpha-value>)",
  // Base background, but can be used for Text in certain situations (e.g inverse of primary text color)
  "base-1": "rgb(var(--color-base-1) / <alpha-value>)",
  "base-2": "rgb(var(--color-base-2) / <alpha-value>)",
  // Grays
  "gray-1": "rgb(var(--color-gray-1) / <alpha-value>)",
  "gray-2": "rgb(var(--color-gray-2) / <alpha-value>)",
  "gray-3": "rgb(var(--color-gray-3) / <alpha-value>)",
  // Blues
  "blue-1": "rgb(var(--color-blue-1) / <alpha-value>)",
  "blue-2": "rgb(var(--color-blue-2) / <alpha-value>)",
  "blue-3": "rgb(var(--color-blue-3) / <alpha-value>)",
  "blue-4": "rgb(var(--color-blue-4) / <alpha-value>)",
  "blue-5": "rgb(var(--color-blue-5) / <alpha-value>)",
  // Greens
  "green-1": "rgb(var(--color-green-1) / <alpha-value>)",
  "green-2": "rgb(var(--color-green-2) / <alpha-value>)",
  "green-3": "rgb(var(--color-green-3) / <alpha-value>)",
  // Yellows
  "yellow-1": "rgb(var(--color-yellow-1) / <alpha-value>)",
  "yellow-2": "rgb(var(--color-yellow-2) / <alpha-value>)",
  // Reds
  "red-1": "rgb(var(--color-red-1) / <alpha-value>)",
  "red-2": "rgb(var(--color-red-2) / <alpha-value>)",
  "red-3": "rgb(var(--color-red-3) / <alpha-value>)",
  // Accents
  "accent-red": "rgb(var(--color-accent-red) / <alpha-value>)",
  "accent-purple": "rgb(var(--color-accent-purple) / <alpha-value>)",
  "accent-fuchsia": "rgb(var(--color-accent-fuchsia) / <alpha-value>)",
  "accent-orange": "rgb(var(--color-accent-orange) / <alpha-value>)",
  "accent-cerulean": "rgb(var(--color-accent-cerulean) / <alpha-value>)",
  "accent-aqua": "rgb(var(--color-accent-aqua) / <alpha-value>)",
  // Aliases
  success: "rgb(var(--color-green-2) / <alpha-value>)",
  warning: "rgb(var(--color-yellow-1) / <alpha-value>)",
  error: "rgb(var(--color-red-2) / <alpha-value>)"
};
var TEXT_COLORS = {
  ...COMMON_COLORS,
  // Base text
  primary: "rgb(var(--color-text-primary) / <alpha-value>)",
  secondary: "rgb(var(--color-text-secondary) / <alpha-value>)",
  // Interactive text
  "interactive-primary": "rgb(var(--color-text-interactive-primary) / <alpha-value>)",
  "interactive-primary-hover": "rgb(var(--color-text-interactive-primary-hover) / <alpha-value>)",
  "interactive-secondary": "rgb(var(--color-text-interactive-secondary) / <alpha-value>)",
  "interactive-secondary-hover": "rgb(var(--color-text-interactive-secondary-hover) / <alpha-value>)",
  // Interactive icons
  "interactive-icon-primary": "rgb(var(--color-interactive-icon-primary) / <alpha-value>)",
  "interactive-icon-primary-hover": "rgb(var(--color-interactive-icon-primary-hover) / <alpha-value>)",
  "interactive-icon-secondary": "rgb(var(--color-interactive-icon-secondary) / <alpha-value>)",
  "interactive-icon-secondary-hover": "rgb(var(--color-interactive-icon-secondary-hover) / <alpha-value>)",
  // Used to match elevation background color for usages related to currentColor i.e. svgs
  "bg-elevation-1": "rgb(var(--color-elevation-1) / <alpha-value>)",
  "bg-elevation-1-subtle": "rgb(var(--color-elevation-1-subtle) / <alpha-value>)",
  "bg-elevation-2": "rgb(var(--color-elevation-2) / <alpha-value>)",
  "bg-elevation-3": "rgb(var(--color-elevation-3) / <alpha-value>)"
};
var BORDER_COLORS = {
  ...COMMON_COLORS,
  // Naming primarily to not conflict with border size presets
  "level-1": "var(--color-border-1)",
  "level-2": "var(--color-border-2)",
  "level-3": "var(--color-border-3)",
  "text-primary": "rgb(var(--color-text-primary) / <alpha-value>)",
  "text-secondary": "rgb(var(--color-text-secondary) / <alpha-value>)"
};
var tailwindBase = {
  content: [],
  darkMode: "class",
  theme: {
    colors: {
      ...COMMON_COLORS,
      // Component background grays
      "component-gray-1": "var(--color-component-gray-1)",
      "component-gray-2": "var(--color-component-gray-2)",
      "component-gray-3": "var(--color-component-gray-3)",
      "component-gray-4": "var(--color-component-gray-4)",
      "component-gray-1-solid": "rgb(var(--color-component-gray-1-solid) / <alpha-value>)",
      "component-gray-2-solid": "rgb(var(--color-component-gray-2-solid) / <alpha-value>)",
      // Elevation
      "elevation-1": "rgb(var(--color-elevation-1) / <alpha-value>)",
      "elevation-1-subtle": "rgb(var(--color-elevation-1-subtle) / <alpha-value>)",
      "elevation-2": "rgb(var(--color-elevation-2) / <alpha-value>)",
      "elevation-3": "rgb(var(--color-elevation-3) / <alpha-value>)",
      // In case we need to use border colors as background colors e.g. Separator
      "border-1": "var(--color-border-1)",
      "border-2": "var(--color-border-2)",
      "border-3": "var(--color-border-3)",
      // In case we need to use text colors as background colors, ex. underlines
      "text-primary": "rgb(var(--color-text-primary) / <alpha-value>)",
      "text-secondary": "rgb(var(--color-text-secondary) / <alpha-value>)"
    },
    textColor: TEXT_COLORS,
    borderColor: BORDER_COLORS,
    fill: TEXT_COLORS,
    boxShadow: {
      // Default tailwind shadow-none
      none: "box-shadow: 0 0 #0000",
      "elevation-1": "var(--shadow-elevation-1)",
      "elevation-1-subtle": "var(--shadow-elevation-1-subtle)",
      "elevation-2": "var(--shadow-elevation-2)",
      "elevation-3": "var(--shadow-elevation-3)"
    },
    fontFamily: {
      inter: "var(--font-family-inter)",
      "ibm-plex-mono": "var(--font-family-ibm-plex-mono)"
    },
    screens: {
      ...Object.fromEntries(
        Object.entries(breakpoints).map(([key, value]) => [
          key,
          numberToPx(value)
        ])
      ),
      // Fixed-range breakpoints
      "xs-sm": {
        min: numberToPx(breakpoints.xs),
        max: numberToPx(breakpoints.sm - 1)
      },
      "xs-lg": {
        min: numberToPx(breakpoints.xs),
        max: numberToPx(breakpoints.lg - 1)
      }
      // NOTE: Add more ranges here as needed (e.g. sm-md, xs-lg, etc.)
    },
    fontSize: {
      // Body
      xs: "12px",
      sm: "14px",
      md: "16px",
      // Heading
      "heading-xs": "18px",
      "heading-sm": "20px",
      "heading-md": "24px",
      "heading-lg": "32px",
      // Display
      "display-sm": "24px",
      "display-md": "32px",
      "display-lg": "48px",
      "display-xl": "60px"
    },
    aspectRatio: {
      auto: "auto",
      square: "1 / 1",
      video: "16 / 9",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
      5: "5",
      6: "6",
      7: "7",
      8: "8",
      9: "9",
      10: "10",
      11: "11",
      12: "12",
      13: "13",
      14: "14",
      15: "15",
      16: "16"
    },
    extend: {
      borderRadius: {
        inherit: "inherit",
        initial: "initial",
        // This default value is in flight (e.g. in discussions to remove 10px entirely), but is what we had before as a "default"
        // so keeping it for now
        default: "10px"
      },
      fontWeight: {
        inherit: "inherit"
      },
      transitionProperty: {
        width: "width"
      },
      zIndex: Z_INDEX
    }
  },
  plugins: [aspectRatio, animate],
  safelist: [
    // Safelisting text colors so we can use them as `color` props in Components e.g. Text
    ...Object.keys(TEXT_COLORS).map((key) => `text-${key}`),
    // Safelisting fill colors so we can use them as `fill` props in Components e.g. SVGs
    ...Object.keys(TEXT_COLORS).map((key) => `fill-${key}`),
    // Safelisting border colors so we can use them as `color` props in Components e.g. Spinner
    ...Object.keys(BORDER_COLORS).map((key) => `border-${key}`),
    // Safelist aspect-ratio classes so they can be used dynamically at runtime (some uses require response from API)
    {
      pattern: /aspect-+/
    },
    // Safelist object fit classes to be used dynamically.
    ...[
      "object-contain",
      "object-cover",
      "object-fill",
      "object-none",
      "object-scale-down"
    ]
  ],
  future: {
    // This makes it such that hover styles are only applied when the user's browser supports hover e.g. desktop
    // https://github.com/tailwindlabs/tailwindcss/pull/8394#issue-1243295337
    hoverOnlyWhenSupported: true
  }
};

// src/styles/utils/interactive.ts
import { tv } from "tailwind-variants";
var interactiveVariants = tv({
  base: "cursor-pointer",
  variants: {
    text: {
      primary: "text-interactive-primary hover:text-interactive-primary-hover",
      secondary: "text-interactive-secondary hover:text-interactive-secondary-hover"
    },
    icon: {
      primary: "text-interactive-icon-primary hover:text-interactive-icon-primary-hover",
      secondary: "text-interactive-icon-secondary hover:text-interactive-icon-secondary-hover"
    },
    iconFill: {
      primary: "fill-interactive-icon-primary hover:fill-interactive-icon-primary-hover",
      secondary: "fill-interactive-icon-secondary hover:fill-interactive-icon-secondary-hover"
    }
  }
});

// src/styles/utils/fill.ts
var allTailwindFills = fromEntries(
  keys(tailwindBase.theme.fill).map((fill2) => [fill2, `fill-${fill2}`])
);
var tailwindFills = allTailwindFills;
var fill = {
  ...tailwindFills,
  // Special "fill"s that handle the whole primary and secondary interactive styles
  "interactive-icon-primary-styles": interactiveVariants({
    iconFill: "primary"
  }),
  "interactive-icon-secondary-styles": interactiveVariants({
    iconFill: "secondary"
  })
};
var fillVariants = tv2({
  variants: {
    fill
  }
});

// src/icons/chains/ArbitrumLogo/ArbitrumLogo.react.tsx
var ArbitrumLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React.createElement(
  "svg",
  {
    className,
    style: { width: size, height: size },
    viewBox: "0 0 24 24",
    ...rest
  },
  /* @__PURE__ */ React.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M7.97992 8.7774C8.4494 8.01126 9.47044 7.765 10.4195 7.77796L11.5327 7.80677L4.97152 18.3282L5.74486 18.7732L12.3852 7.81685L15.3202 7.80677L8.6971 19.0397L11.4578 20.6281L11.7876 20.8182C11.9302 20.8744 12.0871 20.8773 12.2326 20.8268L14.8435 19.3133L12.0554 14.9382L13.7577 12.0508L17.4184 17.8213L15.6514 18.8453L19.5354 16.5944L18.1385 17.4037L14.2775 11.1579L15.7609 8.64059L19.75 14.8533V16.145C19.7471 16.3193 19.6679 16.4835 19.5354 16.5958L18.1385 17.4051L17.4184 17.8228L14.8435 19.3147L12.2326 20.8283C12.0886 20.8787 11.9302 20.8758 11.7876 20.8196L8.69854 19.0425L8.06777 20.1126L10.8443 21.7111C10.9365 21.7629 11.0186 21.809 11.0848 21.8479C11.1885 21.9055 11.2591 21.9458 11.285 21.9573C11.5168 22.0596 11.7689 22.1114 12.0223 22.1086C12.2556 22.1086 12.486 22.0653 12.7021 21.9804L20.2871 17.588C20.7206 17.251 20.9813 16.7398 21.0014 16.1911V7.79237C20.9741 7.17744 20.6457 6.61579 20.1244 6.29033L12.8259 2.09383C12.2974 1.83461 11.6796 1.83461 11.1525 2.09383C11.092 2.12407 4.05704 6.20392 4.05704 6.20392C3.95912 6.25144 3.86551 6.30617 3.77766 6.36953C3.31539 6.69932 3.0288 7.22208 3 7.78805V16.4777",
      fill: fill2
    }
  )
);

// src/icons/chains/AvalancheLogo/AvalancheLogo.react.tsx
import React2 from "react";
var AvalancheLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React2.createElement(
  "svg",
  {
    className,
    fill: "none",
    height: "20",
    style: { width: size, height: size },
    viewBox: "0 0 20 20",
    width: "20",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React2.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      clipRule: "evenodd",
      d: "M6.0688 17H3.41651C2.85918 17 2.58388 17 2.41603 16.8926C2.23472 16.7751 2.12393 16.5804 2.1105 16.3654C2.10042 16.1674 2.23809 15.9256 2.51338 15.4422L9.06224 3.89893C9.3409 3.40875 9.4819 3.16366 9.65983 3.07302C9.8512 2.97566 10.0795 2.97566 10.2709 3.07302C10.4488 3.16366 10.5898 3.40875 10.8685 3.89893L12.2148 6.24907L12.2217 6.26106C12.5226 6.78692 12.6752 7.05359 12.7419 7.33348C12.8157 7.639 12.8157 7.9613 12.7419 8.26683C12.6747 8.54884 12.5237 8.81744 12.2181 9.35125L8.77819 15.4321L8.76929 15.4477C8.46633 15.9779 8.31279 16.2466 8.10001 16.4493C7.86835 16.671 7.58969 16.832 7.28416 16.9228C7.0055 17 6.69328 17 6.0688 17ZM12.7667 17H16.5672C17.1278 17 17.4099 17 17.5779 16.8894C17.7591 16.7718 17.8732 16.5736 17.8834 16.3589C17.8931 16.1673 17.7584 15.9349 17.4945 15.4795C17.4854 15.464 17.4763 15.4482 17.467 15.4322L15.5634 12.1756L15.5417 12.1389C15.2742 11.6865 15.1392 11.4581 14.9658 11.3698C14.7745 11.2724 14.5494 11.2724 14.3581 11.3698C14.1835 11.4605 14.0425 11.6988 13.7639 12.1789L11.867 15.4356L11.8605 15.4468C11.5828 15.9261 11.444 16.1656 11.454 16.3622C11.4674 16.5771 11.5782 16.7751 11.7595 16.8926C11.924 17 12.2061 17 12.7667 17Z",
      fillRule: "evenodd"
    }
  )
);

// src/icons/chains/BaseLogo/BaseLogo.react.tsx
import React3 from "react";
var BaseLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React3.createElement(
  "svg",
  {
    className,
    fill: "none",
    style: { width: size, height: size },
    viewBox: "0 0 111 111",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React3.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M54.921 110.034C85.359 110.034 110.034 85.402 110.034 55.017C110.034 24.6319 85.359 0 54.921 0C26.0432 0 2.35281 22.1714 0 50.3923H72.8467V59.6416H3.9565e-07C2.35281 87.8625 26.0432 110.034 54.921 110.034Z"
    }
  )
);

// src/icons/chains/BinanceLogo/BinanceLogo.react.tsx
import React4 from "react";
var BinanceLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React4.createElement(
  "svg",
  {
    className,
    fill: "none",
    style: { width: size, height: size },
    viewBox: "0 0 21 24",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React4.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M4 3.68L10.37 0L16.74 3.68L14.4 5.04L10.37 2.72L6.34 5.04L4 3.68V3.68ZM16.74 8.32L14.4 6.96L10.37 9.28L6.34 6.96L4 8.32V11.04L8.03 13.36V18L10.37 19.36L12.71 18V13.36L16.74 11.04V8.32V8.32ZM16.74 15.67V12.95L14.4 14.31V17.03L16.74 15.67V15.67ZM18.4 16.63L14.37 18.95V21.67L20.74 17.99V10.64L18.4 12V16.64V16.63ZM16.06 6L18.4 7.36V10.08L20.74 8.72V6L18.4 4.64L16.06 6ZM8.03 19.93V22.65L10.37 24.01L12.71 22.65V19.93L10.37 21.29L8.03 19.93ZM4 15.67L6.34 17.03V14.31L4 12.95V15.67V15.67ZM8.03 5.99L10.37 7.35L12.71 5.99L10.37 4.63L8.03 5.99ZM2.34 7.35L4.68 5.99L2.34 4.63L0 5.99V8.71L2.34 10.07V7.35V7.35ZM2.34 11.99L0 10.63V17.98L6.37 21.66V18.94L2.34 16.62V11.98V11.99Z"
    }
  )
);

// src/icons/chains/EthereumLogo/EthereumLogo.react.tsx
import React5 from "react";

// src/utils/classNames.ts
import { clsx } from "clsx";

// src/utils/twMerge.ts
import { extendTailwindMerge } from "tailwind-merge";
var twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: [
            "xs",
            "sm",
            "md",
            "heading-xs",
            "heading-sm",
            "heading-md",
            "heading-lg",
            "display-sm",
            "display-md",
            "display-lg",
            "display-xl"
          ]
        }
      ],
      z: [
        {
          z: Object.keys(Z_INDEX)
        }
      ],
      "font-weight": ["font-inherit"],
      rounded: ["rounded-inherit", "rounded-initial"]
    }
  }
});

// src/utils/classNames.ts
var classNames = (...inputs) => {
  return twMerge(clsx(inputs));
};

// src/icons/chains/EthereumLogo/EthereumLogo.react.tsx
var EthereumLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React5.createElement(
  "svg",
  {
    className: classNames(fillVariants({ fill: fill2 }), className),
    style: { width: size, height: size },
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React5.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M18.527 12.2062L12 16.1938L5.46875 12.2062L12 1L18.527 12.2062ZM12 17.4742L5.46875 13.4867L12 23L18.5312 13.4867L12 17.4742V17.4742Z"
    }
  )
);

// src/icons/chains/KlaytnLogo/KlaytnLogo.react.tsx
import React6 from "react";
var KlaytnLogo = ({
  className,
  fill: fill2 = "current",
  size = 20
}) => /* @__PURE__ */ React6.createElement(
  "svg",
  {
    className,
    fill: "none",
    height: "24",
    style: { width: size, height: size },
    viewBox: "0 0 24 24",
    width: "24",
    xmlns: "http://www.w3.org/2000/svg"
  },
  /* @__PURE__ */ React6.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M18.9724 5.95801L12.9745 11.9133L18.9724 17.8658C21.9328 14.4592 21.9328 9.36464 18.9724 5.95801Z"
    }
  ),
  /* @__PURE__ */ React6.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M11.985 21L17.7743 18.5992L11.985 12.8525L6.19666 18.5992L11.985 21Z"
    }
  ),
  /* @__PURE__ */ React6.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M12.4666 3L7.20337 15.7014L11.4907 11.4168L17.7725 5.17857L12.4666 3Z"
    }
  ),
  /* @__PURE__ */ React6.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M10.3815 4.54102L2.81995 12.0635C2.85078 14.146 3.58816 16.1286 4.91689 17.7177L10.3815 4.54102Z"
    }
  )
);

// src/icons/chains/OptimismLogo/OptimismLogo.react.tsx
import React7 from "react";
var OptimismLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React7.createElement(
  "svg",
  {
    className,
    fill: "none",
    height: "24",
    style: { width: size, height: size },
    viewBox: "0 0 24 24",
    width: "24",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React7.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M4.73712 18.316C3.34521 18.316 2.20484 17.9884 1.31589 17.3334C0.43863 16.6667 0 15.7193 0 14.4912C0 14.2338 0.0292665 13.918 0.0877068 13.5437C0.239839 12.7016 0.456209 11.6898 0.736909 10.5084C1.53226 7.29187 3.58505 5.68359 6.89521 5.68359C7.79584 5.68359 8.60288 5.83563 9.31641 6.13971C10.0299 6.4321 10.5913 6.87662 11.0007 7.47317C11.4101 8.05795 11.6148 8.7598 11.6148 9.57852C11.6148 9.82416 11.5856 10.1341 11.527 10.5084C11.3516 11.5494 11.1411 12.5612 10.8954 13.5437C10.4861 15.1462 9.77841 16.3451 8.77249 17.1404C7.76657 17.9241 6.42151 18.316 4.73712 18.316ZM4.98276 15.7895C5.63785 15.7895 6.19336 15.5965 6.64957 15.2105C7.11747 14.8245 7.45081 14.2338 7.6496 13.4385C7.91861 12.339 8.12339 11.3798 8.26374 10.5611C8.31049 10.3154 8.33386 10.064 8.33386 9.80658C8.33386 8.74222 7.77826 8.20999 6.66715 8.20999C6.01206 8.20999 5.45065 8.40298 4.98276 8.78897C4.52664 9.17505 4.1991 9.76572 4.00031 10.5611C3.78974 11.3447 3.57916 12.3039 3.36869 13.4385C3.32184 13.6723 3.29846 13.918 3.29846 14.1753C3.29846 15.2514 3.85996 15.7895 4.98276 15.7895Z"
    }
  ),
  /* @__PURE__ */ React7.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M12.4202 18.1399C12.2915 18.1399 12.192 18.099 12.1219 18.0171C12.0633 17.9235 12.0459 17.8183 12.0692 17.7013L14.4904 6.29703C14.5138 6.16837 14.5781 6.06308 14.6834 5.98117C14.7887 5.89935 14.8998 5.8584 15.0168 5.8584H19.6838C20.9821 5.8584 22.0231 6.12741 22.8067 6.66543C23.6022 7.20355 23.9998 7.98132 23.9998 8.99893C23.9998 9.29132 23.9648 9.59549 23.8946 9.91125C23.6022 11.2564 23.0115 12.2506 22.1225 12.894C21.2453 13.5373 20.0405 13.8589 18.5082 13.8589H16.1397L15.3326 17.7013C15.3092 17.83 15.2448 17.9352 15.1396 18.0171C15.0343 18.099 14.9232 18.1399 14.8063 18.1399H12.4202ZM18.6311 11.4377C19.1223 11.4377 19.5492 11.3032 19.9118 11.0341C20.2861 10.7651 20.5318 10.3792 20.6487 9.87619C20.6838 9.67731 20.7014 9.50189 20.7014 9.34985C20.7014 9.01062 20.602 8.7533 20.4031 8.57788C20.2042 8.39069 19.8651 8.29718 19.3855 8.29718H17.28L16.6134 11.4377H18.6311Z"
    }
  )
);

// src/icons/chains/PolygonLogo/PolygonLogo.react.tsx
import React8 from "react";
var PolygonLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React8.createElement(
  "svg",
  {
    className,
    fill: "none",
    height: "24",
    style: { width: size, height: size },
    viewBox: "0 0 24 24",
    width: "24",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React8.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M17.0436 8.69125C16.6788 8.47701 16.203 8.47701 15.7986 8.69125L12.9437 10.3456L11.0048 11.4247L8.14995 13.0791C7.78516 13.2933 7.30935 13.2933 6.90491 13.0791L4.63689 11.7858C4.2721 11.5715 4.01437 11.1748 4.01437 10.7424V8.19137C4.01437 7.75893 4.23245 7.36616 4.63689 7.14796L6.86923 5.89032C7.23401 5.67608 7.70982 5.67608 8.11426 5.89032L10.3466 7.14796C10.7114 7.36219 10.9691 7.75893 10.9691 8.19137V9.84574L12.908 8.73092V7.07655C12.908 6.64411 12.69 6.25134 12.2855 6.03314L8.15391 3.66068C7.78912 3.44644 7.31331 3.44644 6.90888 3.66068L2.70589 6.03314C2.30145 6.24737 2.08337 6.64411 2.08337 7.07655V11.8572C2.08337 12.2896 2.30145 12.6824 2.70589 12.9006L6.91284 15.273C7.27763 15.4873 7.75344 15.4873 8.15788 15.273L11.0127 13.6544L12.9517 12.5396L15.8065 10.9209C16.1713 10.7067 16.6471 10.7067 17.0516 10.9209L19.2839 12.1785C19.6487 12.3928 19.9064 12.7895 19.9064 13.2219V15.7729C19.9064 16.2054 19.6883 16.5981 19.2839 16.8163L17.0516 18.1097C16.6868 18.3239 16.211 18.3239 15.8065 18.1097L13.5742 16.852C13.2094 16.6378 12.9517 16.2411 12.9517 15.8086V14.1543L11.0127 15.2691V16.9235C11.0127 17.3559 11.2308 17.7487 11.6353 17.9669L15.8422 20.3393C16.207 20.5536 16.6828 20.5536 17.0872 20.3393L21.2942 17.9669C21.659 17.7526 21.9167 17.3559 21.9167 16.9235V12.1428C21.9167 11.7104 21.6986 11.3176 21.2942 11.0994L17.0436 8.69125Z"
    }
  )
);

// src/icons/chains/SolanaLogo/SolanaLogo.react.tsx
import React9 from "react";
var SolanaLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React9.createElement(
  "svg",
  {
    className,
    fill: "none",
    height: "24",
    style: { width: size, height: size },
    viewBox: "0 0 24 24",
    width: "24",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React9.createElement(
    "path",
    {
      className: fillVariants({ fill: fill2 }),
      d: "M20.8513 16.615L17.898 19.7817C17.8341 19.8505 17.7568 19.9054 17.6707 19.9429C17.5847 19.9805 17.4919 19.9999 17.398 20H3.39807C3.3313 20 3.26599 19.9805 3.21013 19.9439C3.15427 19.9073 3.1103 19.8552 3.08359 19.794C3.05688 19.7328 3.0486 19.6652 3.05976 19.5993C3.07091 19.5335 3.10102 19.4723 3.1464 19.4233L6.09639 16.2567C6.16028 16.1879 6.23763 16.133 6.32366 16.0954C6.40967 16.0579 6.50252 16.0384 6.59639 16.0383H20.5963C20.6637 16.037 20.73 16.0555 20.7868 16.0917C20.8437 16.1279 20.8887 16.1801 20.9158 16.2418C20.9432 16.3034 20.9515 16.3717 20.94 16.4382C20.9285 16.5046 20.8977 16.5661 20.8513 16.615ZM17.898 10.2367C17.8339 10.1682 17.7565 10.1136 17.6705 10.076C17.5845 10.0385 17.4918 10.0189 17.398 10.0184H3.39807C3.3313 10.0184 3.26599 10.0379 3.21013 10.0745C3.15427 10.1111 3.1103 10.1632 3.08359 10.2244C3.05688 10.2856 3.0486 10.3532 3.05976 10.4191C3.07091 10.4849 3.10102 10.5461 3.1464 10.5951L6.09639 13.7634C6.16051 13.8318 6.23793 13.8865 6.32389 13.9241C6.40986 13.9616 6.50259 13.9812 6.59639 13.9817H20.5963C20.663 13.9813 20.728 13.9616 20.7837 13.9249C20.8393 13.8882 20.883 13.8361 20.9095 13.7749C20.936 13.7138 20.9442 13.6463 20.9328 13.5806C20.9217 13.5149 20.8917 13.4539 20.8463 13.405L17.898 10.2367ZM3.39807 7.96171H17.398C17.4919 7.96163 17.5847 7.94218 17.6707 7.90462C17.7568 7.86707 17.8341 7.81217 17.898 7.74338L20.8513 4.57674C20.8977 4.52782 20.9285 4.46629 20.94 4.39987C20.9515 4.33346 20.9432 4.26513 20.9158 4.20349C20.8887 4.14185 20.8437 4.08964 20.7868 4.05345C20.73 4.01725 20.6637 3.99868 20.5963 4.00007H6.59639C6.50252 4.00017 6.40967 4.01961 6.32366 4.05717C6.23763 4.09474 6.16028 4.14962 6.09639 4.21841L3.1464 7.38505C3.10102 7.43403 3.07091 7.4952 3.05976 7.56103C3.0486 7.62687 3.05688 7.69452 3.08359 7.75572C3.1103 7.81692 3.15427 7.869 3.21013 7.90558C3.26599 7.94218 3.3313 7.96168 3.39807 7.96171Z"
    }
  )
);

// src/icons/chains/ZoraLogo/ZoraLogo.react.tsx
import React10 from "react";
var ZoraLogo = ({
  className,
  fill: fill2 = "current",
  size = 20,
  ...rest
}) => /* @__PURE__ */ React10.createElement(
  "svg",
  {
    className: classNames(fillVariants({ fill: fill2 }), className),
    style: { width: size, height: size },
    viewBox: "0 0 28 27",
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React10.createElement("g", { transform: "matrix(1.3333334 0 0 1.3333334 0 0)" }, /* @__PURE__ */ React10.createElement(
    "image",
    {
      height: "20.25",
      preserveAspectRatio: "none",
      width: "21",
      x: "0",
      xlinkHref: "data:image/png;base64,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",
      y: "0"
    }
  ))
);

// src/icons/material-symbols/outlined/360.react.tsx
import React11 from "react";
var ThreeSixty = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React11.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React11.createElement("title", null, "360"),
    /* @__PURE__ */ React11.createElement("path", { d: "m360-160-56-56 70-72q-128-17-211-70T80-480q0-83 115.5-141.5T480-680q169 0 284.5 58.5T880-480q0 62-66.5 111T640-296v-82q77-20 118.5-49.5T800-480q0-32-85.5-76T480-600q-149 0-234.5 44T160-480q0 24 51 57.5T356-372l-52-52 56-56 160 160-160 160Z" })
  );
};

// src/icons/material-symbols/outlined/AccountCircle.react.tsx
import React12 from "react";
var AccountCircle = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React12.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React12.createElement("title", null, "Account Circle"),
    /* @__PURE__ */ React12.createElement("path", { d: "M234-276q51-39 114-61.5T480-360q69 0 132 22.5T726-276q35-41 54.5-93T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 59 19.5 111t54.5 93Zm246-164q-59 0-99.5-40.5T340-580q0-59 40.5-99.5T480-720q59 0 99.5 40.5T620-580q0 59-40.5 99.5T480-440Zm0 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q53 0 100-15.5t86-44.5q-39-29-86-44.5T480-280q-53 0-100 15.5T294-220q39 29 86 44.5T480-160Zm0-360q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm0-60Zm0 360Z" })
  );
};

// src/icons/material-symbols/outlined/Add.react.tsx
import React13 from "react";
var Add = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React13.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React13.createElement("title", null, "Add"),
    /* @__PURE__ */ React13.createElement("path", { d: "M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" })
  );
};

// src/icons/material-symbols/outlined/Apps/Apps.react.tsx
import React14 from "react";
var Apps = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React14.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React14.createElement("title", null, "Apps"),
    /* @__PURE__ */ React14.createElement("path", { d: "M240-160q-33 0-56.5-23.5T160-240q0-33 23.5-56.5T240-320q33 0 56.5 23.5T320-240q0 33-23.5 56.5T240-160Zm240 0q-33 0-56.5-23.5T400-240q0-33 23.5-56.5T480-320q33 0 56.5 23.5T560-240q0 33-23.5 56.5T480-160Zm240 0q-33 0-56.5-23.5T640-240q0-33 23.5-56.5T720-320q33 0 56.5 23.5T800-240q0 33-23.5 56.5T720-160ZM240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400ZM240-640q-33 0-56.5-23.5T160-720q0-33 23.5-56.5T240-800q33 0 56.5 23.5T320-720q0 33-23.5 56.5T240-640Zm240 0q-33 0-56.5-23.5T400-720q0-33 23.5-56.5T480-800q33 0 56.5 23.5T560-720q0 33-23.5 56.5T480-640Zm240 0q-33 0-56.5-23.5T640-720q0-33 23.5-56.5T720-800q33 0 56.5 23.5T800-720q0 33-23.5 56.5T720-640Z" })
  );
};

// src/icons/material-symbols/outlined/AppsFont600/AppsFont600.react.tsx
import React15 from "react";
var AppsFont600 = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React15.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React15.createElement("title", null, "Apps"),
    /* @__PURE__ */ React15.createElement("path", { d: "M231.596-142.477q-36.466 0-62.792-26.327-26.327-26.326-26.327-62.792 0-36.467 26.327-62.435Q195.13-320 231.596-320q36.467 0 62.435 25.969Q320-268.063 320-231.596q0 36.466-25.969 62.792-25.968 26.327-62.435 26.327Zm248.479 0q-36.466 0-62.51-26.327-26.044-26.326-26.044-62.792 0-36.467 25.969-62.435Q443.458-320 479.925-320q36.466 0 62.51 25.969 26.044 25.968 26.044 62.435 0 36.466-25.969 62.792-25.968 26.327-62.435 26.327Zm248.329 0q-36.467 0-62.435-26.327Q640-195.13 640-231.596q0-36.467 25.969-62.435Q691.937-320 728.404-320q36.466 0 62.792 25.969 26.327 25.968 26.327 62.435 0 36.466-26.327 62.792-26.326 26.327-62.792 26.327ZM231.596-391.521q-36.466 0-62.792-25.969-26.327-25.968-26.327-62.435 0-36.466 26.327-62.51 26.326-26.044 62.792-26.044 36.467 0 62.435 25.969Q320-516.542 320-480.075q0 36.466-25.969 62.51-25.968 26.044-62.435 26.044Zm248.479 0q-36.466 0-62.51-25.969-26.044-25.968-26.044-62.435 0-36.466 25.969-62.51 25.968-26.044 62.435-26.044 36.466 0 62.51 25.969 26.044 25.968 26.044 62.435 0 36.466-25.969 62.51-25.968 26.044-62.435 26.044Zm248.329 0q-36.467 0-62.435-25.969Q640-443.458 640-479.925q0-36.466 25.969-62.51 25.968-26.044 62.435-26.044 36.466 0 62.792 25.969 26.327 25.968 26.327 62.435 0 36.466-26.327 62.51-26.326 26.044-62.792 26.044ZM231.596-640q-36.466 0-62.792-25.969-26.327-25.968-26.327-62.435 0-36.466 26.327-62.792 26.326-26.327 62.792-26.327 36.467 0 62.435 26.327Q320-764.87 320-728.404q0 36.467-25.969 62.435Q268.063-640 231.596-640Zm248.479 0q-36.466 0-62.51-25.969-26.044-25.968-26.044-62.435 0-36.466 25.969-62.792 25.968-26.327 62.435-26.327 36.466 0 62.51 26.327 26.044 26.326 26.044 62.792 0 36.467-25.969 62.435Q516.542-640 480.075-640Zm248.329 0q-36.467 0-62.435-25.969Q640-691.937 640-728.404q0-36.466 25.969-62.792 25.968-26.327 62.435-26.327 36.466 0 62.792 26.327 26.327 26.326 26.327 62.792 0 36.467-26.327 62.435Q764.87-640 728.404-640Z" })
  );
};

// src/icons/material-symbols/outlined/ArrowDropDownFilled/ArrowDropDownFilled.react.tsx
import React16 from "react";
var ArrowDropDownFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React16.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React16.createElement("title", null, "Arrow Drop Down"),
    /* @__PURE__ */ React16.createElement("path", { d: "M480-360 280-560h400L480-360Z" })
  );
};

// src/icons/material-symbols/outlined/ArrowDropUpFilled/ArrowDropUpFilled.react.tsx
import React17 from "react";
var ArrowDropUpFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React17.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React17.createElement("title", null, "Arrow Drop Up"),
    /* @__PURE__ */ React17.createElement("path", { d: "m280-400 200-200 200 200H280Z" })
  );
};

// src/icons/material-symbols/outlined/ArrowBack.react.tsx
import React18 from "react";
var ArrowBack = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React18.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React18.createElement("title", null, "Arrow Back"),
    /* @__PURE__ */ React18.createElement("path", { d: "m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z" })
  );
};

// src/icons/material-symbols/outlined/ArrowDownward.react.tsx
import React19 from "react";
var ArrowDownward = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React19.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React19.createElement("title", null, "Arrow Downward"),
    /* @__PURE__ */ React19.createElement("path", { d: "M440-800v487L216-537l-56 57 320 320 320-320-56-57-224 224v-487h-80Z" })
  );
};

// src/icons/material-symbols/outlined/ArrowForward.react.tsx
import React20 from "react";
var ArrowForward = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React20.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React20.createElement("title", null, "Arrow Forward"),
    /* @__PURE__ */ React20.createElement("path", { d: "M647-440H160v-80h487L423-744l57-56 320 320-320 320-57-56 224-224Z" })
  );
};

// src/icons/material-symbols/outlined/ArrowLeft.react.tsx
import React21 from "react";
var ArrowLeft = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React21.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React21.createElement("title", null, "Arrow Left"),
    /* @__PURE__ */ React21.createElement("path", { d: "M560-280 360-480l200-200v400Z" })
  );
};

// src/icons/material-symbols/outlined/ArrowRight.react.tsx
import React22 from "react";
var ArrowRight = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React22.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React22.createElement("title", null, "Arrow Right"),
    /* @__PURE__ */ React22.createElement("path", { d: "M400-280v-400l200 200-200 200Z" })
  );
};

// src/icons/material-symbols/outlined/Autorenew.react.tsx
import React23 from "react";
var Autorenew = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React23.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React23.createElement("title", null, "Autorenew"),
    /* @__PURE__ */ React23.createElement("path", { d: "M204-318q-22-38-33-78t-11-82q0-134 93-228t227-94h7l-64-64 56-56 160 160-160 160-56-56 64-64h-7q-100 0-170 70.5T240-478q0 26 6 51t18 49l-60 60ZM481-40 321-200l160-160 56 56-64 64h7q100 0 170-70.5T720-482q0-26-6-51t-18-49l60-60q22 38 33 78t11 82q0 134-93 228t-227 94h-7l64 64-56 56Z" })
  );
};

// src/icons/material-symbols/outlined/AwardStar.react.tsx
import React24 from "react";
var AwardStar = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React24.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React24.createElement("title", null, "Award star"),
    /* @__PURE__ */ React24.createElement("path", { d: "m363-310 117-71 117 71-31-133 104-90-137-11-53-126-53 126-137 11 104 90-31 133ZM480-28 346-160H160v-186L28-480l132-134v-186h186l134-132 134 132h186v186l132 134-132 134v186H614L480-28Zm0-112 100-100h140v-140l100-100-100-100v-140H580L480-820 380-720H240v140L140-480l100 100v140h140l100 100Zm0-340Z" })
  );
};

// src/icons/material-symbols/outlined/Bolt.react.tsx
import React25 from "react";
var Bolt = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React25.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React25.createElement("title", null, "Bolt"),
    /* @__PURE__ */ React25.createElement("path", { d: "m422-232 207-248H469l29-227-185 267h139l-30 208ZM320-80l40-280H160l360-520h80l-40 320h240L400-80h-80Zm151-390Z" })
  );
};

// src/icons/material-symbols/outlined/Calculate.react.tsx
import React26 from "react";
var Calculate = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React26.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React26.createElement("title", null, "Calculate"),
    /* @__PURE__ */ React26.createElement("path", { d: "M320-240h60v-80h80v-60h-80v-80h-60v80h-80v60h80v80Zm200-30h200v-60H520v60Zm0-100h200v-60H520v60Zm44-152 56-56 56 56 42-42-56-58 56-56-42-42-56 56-56-56-42 42 56 56-56 58 42 42Zm-314-70h200v-60H250v60Zm-50 472q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z" })
  );
};

// src/icons/material-symbols/outlined/CalendarAddOn.react.tsx
import React27 from "react";
var CalendarAddOn = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React27.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React27.createElement("title", null, "Calendar add on"),
    /* @__PURE__ */ React27.createElement("path", { d: "M680-80v-120H560v-80h120v-120h80v120h120v80H760v120h-80Zm-480-80q-33 0-56.5-23.5T120-240v-480q0-33 23.5-56.5T200-800h40v-80h80v80h240v-80h80v80h40q33 0 56.5 23.5T760-720v244q-20-3-40-3t-40 3v-84H200v320h280q0 20 3 40t11 40H200Zm0-480h480v-80H200v80Zm0 0v-80 80Z" })
  );
};

// src/icons/material-symbols/outlined/CalendarToday.react.tsx
import React28 from "react";
var CalendarToday = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React28.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React28.createElement("title", null, "Calendar Today"),
    /* @__PURE__ */ React28.createElement("path", { d: "M200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Zm0-480h560v-80H200v80Zm0 0v-80 80Z" })
  );
};

// src/icons/material-symbols/outlined/CalendarTodayFilled/CalendarTodayFilled.react.tsx
import React29 from "react";
var CalendarTodayFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React29.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React29.createElement("title", null, "Calendar Today"),
    /* @__PURE__ */ React29.createElement("path", { d: "M200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Z" })
  );
};

// src/icons/material-symbols/outlined/CancelFilled.react.tsx
import React30 from "react";
var CancelFilled = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React30.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React30.createElement("title", null, "Cancel"),
    /* @__PURE__ */ React30.createElement("path", { d: "m336-280 144-144 144 144 56-56-144-144 144-144-56-56-144 144-144-144-56 56 144 144-144 144 56 56ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/Check.react.tsx
import React31 from "react";
var Check = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React31.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React31.createElement("title", null, "Check"),
    /* @__PURE__ */ React31.createElement("path", { d: "M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" })
  );
};

// src/icons/material-symbols/outlined/CheckCircleFilled.react.tsx
import React32 from "react";
var CheckCircleFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React32.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React32.createElement("title", null, "Check Circle"),
    /* @__PURE__ */ React32.createElement("path", { d: "m424-296 282-282-56-56-226 226-114-114-56 56 170 170Zm56 216q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/ChevronLeft.react.tsx
import React33 from "react";
var ChevronLeft = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React33.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React33.createElement("title", null, "Chevron Left"),
    /* @__PURE__ */ React33.createElement("path", { d: "M561-240 320-481l241-241 43 43-198 198 198 198-43 43Z" })
  );
};

// src/icons/material-symbols/outlined/ChevronRight.react.tsx
import React34 from "react";
var ChevronRight = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React34.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React34.createElement("title", null, "Chevron Right"),
    /* @__PURE__ */ React34.createElement("path", { d: "M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" })
  );
};

// src/icons/material-symbols/outlined/CircleFilled.react.tsx
import React35 from "react";
var CircleFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React35.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React35.createElement("title", null, "Circle Filled"),
    /* @__PURE__ */ React35.createElement("path", { d: "M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-156t86-127Q252-817 325-848.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 82-31.5 155T763-197.5q-54 54.5-127 86T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/Close.react.tsx
import React36 from "react";
var Close = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Close",
  ...rest
}) => {
  return /* @__PURE__ */ React36.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React36.createElement("title", null, title),
    /* @__PURE__ */ React36.createElement("path", { d: "m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" })
  );
};

// src/icons/material-symbols/outlined/DarkMode/DarkMode.react.tsx
import React37 from "react";
var DarkMode = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React37.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React37.createElement("title", null, "Dark Mode"),
    /* @__PURE__ */ React37.createElement("path", { d: "M480-120q-150 0-255-105T120-480q0-150 105-255t255-105q14 0 27.5 1t26.5 3q-41 29-65.5 75.5T444-660q0 90 63 153t153 63q55 0 101-24.5t75-65.5q2 13 3 26.5t1 27.5q0 150-105 255T480-120Zm0-80q88 0 158-48.5T740-375q-20 5-40 8t-40 3q-123 0-209.5-86.5T364-660q0-20 3-40t8-40q-78 32-126.5 102T200-480q0 116 82 198t198 82Zm-10-270Z" })
  );
};

// src/icons/material-symbols/outlined/Delete.react.tsx
import React38 from "react";
var Delete = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React38.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React38.createElement("title", null, "Delete"),
    /* @__PURE__ */ React38.createElement("path", { d: "M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z" })
  );
};

// src/icons/material-symbols/outlined/DoNotDisturbOn.react.tsx
import React39 from "react";
var DoNotDisturbOn = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React39.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React39.createElement("title", null, "Do not disturb on"),
    /* @__PURE__ */ React39.createElement("path", { d: "M280-440h400v-80H280v80ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" })
  );
};

// src/icons/material-symbols/outlined/Edit.react.tsx
import React40 from "react";
var Edit = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React40.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React40.createElement("title", null, "Edit"),
    /* @__PURE__ */ React40.createElement("path", { d: "M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" })
  );
};

// src/icons/material-symbols/outlined/EmojiPeople.react.tsx
import React41 from "react";
var EmojiPeople = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React41.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React41.createElement("title", null, "Emoji people"),
    /* @__PURE__ */ React41.createElement("path", { d: "M360-80v-529q-91-24-145.5-100.5T160-880h80q0 83 53.5 141.5T430-680h100q30 0 56 11t47 32l181 181-56 56-158-158v478h-80v-240h-80v240h-80Zm120-640q-33 0-56.5-23.5T400-800q0-33 23.5-56.5T480-880q33 0 56.5 23.5T560-800q0 33-23.5 56.5T480-720Z" })
  );
};

// src/icons/material-symbols/outlined/Error.react.tsx
import React42 from "react";
var Error = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Error",
  ...rest
}) => {
  return /* @__PURE__ */ React42.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React42.createElement("title", null, title),
    /* @__PURE__ */ React42.createElement("path", { d: "M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" })
  );
};

// src/icons/material-symbols/outlined/ErrorFilled.react.tsx
import React43 from "react";
var ErrorFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Error",
  ...rest
}) => {
  return /* @__PURE__ */ React43.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React43.createElement("title", null, title),
    /* @__PURE__ */ React43.createElement("path", { d: "M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/ExpandAll.react.tsx
import React44 from "react";
var ExpandAll = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React44.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React44.createElement("title", null, "Expand all"),
    /* @__PURE__ */ React44.createElement("path", { d: "M480-80 240-320l57-57 183 183 183-183 57 57L480-80ZM298-584l-58-56 240-240 240 240-58 56-182-182-182 182Z" })
  );
};

// src/icons/material-symbols/outlined/ExpandLess.react.tsx
import React45 from "react";
var ExpandLess = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React45.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React45.createElement("title", null, "Expand less"),
    /* @__PURE__ */ React45.createElement("path", { d: "m296-345-56-56 240-240 240 240-56 56-184-184-184 184Z" })
  );
};

// src/icons/material-symbols/outlined/ExpandMore.react.tsx
import React46 from "react";
var ExpandMore = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React46.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React46.createElement("title", null, "Expand more"),
    /* @__PURE__ */ React46.createElement("path", { d: "M480-345 240-585l56-56 184 184 184-184 56 56-240 240Z" })
  );
};

// src/icons/material-symbols/outlined/Explore/Explore.react.tsx
import React47 from "react";
var Explore = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Explore",
  ...rest
}) => {
  return /* @__PURE__ */ React47.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React47.createElement("title", null, title),
    /* @__PURE__ */ React47.createElement("path", { d: "m260-260 300-140 140-300-300 140-140 300Zm220-180q-17 0-28.5-11.5T440-480q0-17 11.5-28.5T480-520q17 0 28.5 11.5T520-480q0 17-11.5 28.5T480-440Zm0 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" })
  );
};

// src/icons/material-symbols/outlined/ExploreFilled/ExploreFilled.react.tsx
import React48 from "react";
var ExploreFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Explore",
  ...rest
}) => {
  return /* @__PURE__ */ React48.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React48.createElement("title", null, title),
    /* @__PURE__ */ React48.createElement("path", { d: "m260-260 300-140 140-300-300 140-140 300Zm220-180q-17 0-28.5-11.5T440-480q0-17 11.5-28.5T480-520q17 0 28.5 11.5T520-480q0 17-11.5 28.5T480-440Zm0 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/Favorite.react.tsx
import React49 from "react";
var Favorite = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React49.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React49.createElement("title", null, "Favorite"),
    /* @__PURE__ */ React49.createElement("path", { d: "m480-120-58-52q-101-91-167-157T150-447.5Q111-500 95.5-544T80-634q0-94 63-157t157-63q52 0 99 22t81 62q34-40 81-62t99-22q94 0 157 63t63 157q0 46-15.5 90T810-447.5Q771-395 705-329T538-172l-58 52Zm0-108q96-86 158-147.5t98-107q36-45.5 50-81t14-70.5q0-60-40-100t-100-40q-47 0-87 26.5T518-680h-76q-15-41-55-67.5T300-774q-60 0-100 40t-40 100q0 35 14 70.5t50 81q36 45.5 98 107T480-228Zm0-273Z" })
  );
};

// src/icons/material-symbols/outlined/FeaturedVideo.react.tsx
import React50 from "react";
var FeaturedVideo = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React50.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React50.createElement("title", null, "Featured video"),
    /* @__PURE__ */ React50.createElement("path", { d: "M200-400h360v-280H200v280Zm-40 240q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z" })
  );
};

// src/icons/material-symbols/outlined/FeatureSearch.react.tsx
import React51 from "react";
var FeatureSearch = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React51.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React51.createElement("title", null, "Feature search"),
    /* @__PURE__ */ React51.createElement("path", { d: "m720-430 80 80v190q0 33-23.5 56.5T720-80H160q-33 0-56.5-23.5T80-160v-560q0-33 23.5-56.5T160-800h220q-8 18-12 38.5t-6 41.5H160v560h560v-270Zm52-174 128 128-56 56-128-128q-21 12-45 20t-51 8q-75 0-127.5-52.5T440-700q0-75 52.5-127.5T620-880q75 0 127.5 52.5T800-700q0 27-8 51t-20 45Zm-152 4q42 0 71-29t29-71q0-42-29-71t-71-29q-42 0-71 29t-29 71q0 42 29 71t71 29ZM160-430v270-560 280-12 22Z" })
  );
};

// src/icons/material-symbols/outlined/FilterList/FilterList.react.tsx
import React52 from "react";
var FilterList = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React52.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React52.createElement("title", null, "Filter List"),
    /* @__PURE__ */ React52.createElement("path", { d: "M400-240v-80h160v80H400ZM240-440v-80h480v80H240ZM120-640v-80h720v80H120Z" })
  );
};

// src/icons/material-symbols/outlined/Flag.react.tsx
import React53 from "react";
var Flag = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React53.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React53.createElement("title", null, "Flag"),
    /* @__PURE__ */ React53.createElement("path", { d: "M200-120v-680h360l16 80h224v400H520l-16-80H280v280h-80Zm300-440Zm86 160h134v-240H510l-16-80H280v240h290l16 80Z" })
  );
};

// src/icons/material-symbols/outlined/FormatListBulleted/FormatListBulleted.react.tsx
import React54 from "react";
var FormatListBulleted = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React54.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React54.createElement("title", null, "Format List Bulleted"),
    /* @__PURE__ */ React54.createElement("path", { d: "M360-200v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360ZM200-160q-33 0-56.5-23.5T120-240q0-33 23.5-56.5T200-320q33 0 56.5 23.5T280-240q0 33-23.5 56.5T200-160Zm0-240q-33 0-56.5-23.5T120-480q0-33 23.5-56.5T200-560q33 0 56.5 23.5T280-480q0 33-23.5 56.5T200-400Zm0-240q-33 0-56.5-23.5T120-720q0-33 23.5-56.5T200-800q33 0 56.5 23.5T280-720q0 33-23.5 56.5T200-640Z" })
  );
};

// src/icons/material-symbols/outlined/Grid3x3.react.tsx
import React55 from "react";
var Grid3x3 = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React55.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React55.createElement("title", null, "Grid 3x3"),
    /* @__PURE__ */ React55.createElement("path", { d: "M320-160v-160H160v-80h160v-160H160v-80h160v-160h80v160h160v-160h80v160h160v80H640v160h160v80H640v160h-80v-160H400v160h-80Zm80-240h160v-160H400v160Z" })
  );
};

// src/icons/material-symbols/outlined/Home.react.tsx
import React56 from "react";
var Home = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React56.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React56.createElement("title", null, "Home"),
    /* @__PURE__ */ React56.createElement("path", { d: "M240-200h120v-240h240v240h120v-360L480-740 240-560v360Zm-80 80v-480l320-240 320 240v480H520v-240h-80v240H160Zm320-350Z" })
  );
};

// src/icons/material-symbols/outlined/HomeFilled/HomeFilled.react.tsx
import React57 from "react";
var HomeFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React57.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React57.createElement("title", null, "Home"),
    /* @__PURE__ */ React57.createElement("path", { d: "M160-120v-480l320-240 320 240v480H560v-280H400v280H160Z" })
  );
};

// src/icons/material-symbols/outlined/ImportContacts/ImportContacts.react.tsx
import React58 from "react";
var ImportContacts = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React58.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React58.createElement("title", null, "Import Contacts"),
    /* @__PURE__ */ React58.createElement("path", { d: "M260-320q47 0 91.5 10.5T440-278v-394q-41-24-87-36t-93-12q-36 0-71.5 7T120-692v396q35-12 69.5-18t70.5-6Zm260 42q44-21 88.5-31.5T700-320q36 0 70.5 6t69.5 18v-396q-33-14-68.5-21t-71.5-7q-47 0-93 12t-87 36v394Zm-40 118q-48-38-104-59t-116-21q-42 0-82.5 11T100-198q-21 11-40.5-1T40-234v-482q0-11 5.5-21T62-752q46-24 96-36t102-12q58 0 113.5 15T480-740q51-30 106.5-45T700-800q52 0 102 12t96 36q11 5 16.5 15t5.5 21v482q0 23-19.5 35t-40.5 1q-37-20-77.5-31T700-240q-60 0-116 21t-104 59ZM280-494Z" })
  );
};

// src/icons/material-symbols/outlined/InfoFilled.react.tsx
import React59 from "react";
var InfoFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React59.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React59.createElement("title", null, "Info"),
    /* @__PURE__ */ React59.createElement("path", { d: "M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/Language/Language.react.tsx
import React60 from "react";
var Language = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Language",
  ...rest
}) => {
  return /* @__PURE__ */ React60.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React60.createElement("title", null, title),
    /* @__PURE__ */ React60.createElement("path", { d: "M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q83 0 155.5 31.5t127 86q54.5 54.5 86 127T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Zm0-82q26-36 45-75t31-83H404q12 44 31 83t45 75Zm-104-16q-18-33-31.5-68.5T322-320H204q29 50 72.5 87t99.5 55Zm208 0q56-18 99.5-55t72.5-87H638q-9 38-22.5 73.5T584-178ZM170-400h136q-3-20-4.5-39.5T300-480q0-21 1.5-40.5T306-560H170q-5 20-7.5 39.5T160-480q0 21 2.5 40.5T170-400Zm216 0h188q3-20 4.5-39.5T580-480q0-21-1.5-40.5T574-560H386q-3 20-4.5 39.5T380-480q0 21 1.5 40.5T386-400Zm268 0h136q5-20 7.5-39.5T800-480q0-21-2.5-40.5T790-560H654q3 20 4.5 39.5T660-480q0 21-1.5 40.5T654-400Zm-16-240h118q-29-50-72.5-87T584-782q18 33 31.5 68.5T638-640Zm-234 0h152q-12-44-31-83t-45-75q-26 36-45 75t-31 83Zm-200 0h118q9-38 22.5-73.5T376-782q-56 18-99.5 55T204-640Z" })
  );
};

// src/icons/material-symbols/outlined/Leaderboard.react.tsx
import React61 from "react";
var Leaderboard = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React61.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React61.createElement("title", null, "Leaderboard"),
    /* @__PURE__ */ React61.createElement("path", { d: "M160-200h160v-320H160v320Zm240 0h160v-560H400v560Zm240 0h160v-240H640v240ZM80-120v-480h240v-240h320v320h240v400H80Z" })
  );
};

// src/icons/material-symbols/outlined/LeaderboardFilled.react.tsx
import React62 from "react";
var LeaderboardFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React62.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React62.createElement("title", null, "Leaderboard Filled"),
    /* @__PURE__ */ React62.createElement("path", { d: "M80-120v-480h220v480H80Zm290 0v-720h220v720H370Zm290 0v-400h220v400H660Z" })
  );
};

// src/icons/material-symbols/outlined/LightMode/LightMode.react.tsx
import React63 from "react";
var LightMode = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React63.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React63.createElement("title", null, "Light Mode"),
    /* @__PURE__ */ React63.createElement("path", { d: "M480-360q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Zm0 80q-83 0-141.5-58.5T280-480q0-83 58.5-141.5T480-680q83 0 141.5 58.5T680-480q0 83-58.5 141.5T480-280ZM200-440H40v-80h160v80Zm720 0H760v-80h160v80ZM440-760v-160h80v160h-80Zm0 720v-160h80v160h-80ZM256-650l-101-97 57-59 96 100-52 56Zm492 496-97-101 53-55 101 97-57 59Zm-98-550 97-101 59 57-100 96-56-52ZM154-212l101-97 55 53-97 101-59-57Zm326-268Z" })
  );
};

// src/icons/material-symbols/outlined/Link.react.tsx
import React64 from "react";
var Link = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React64.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React64.createElement("title", null, "Link"),
    /* @__PURE__ */ React64.createElement("path", { d: "M440-280H280q-83 0-141.5-58.5T80-480q0-83 58.5-141.5T280-680h160v80H280q-50 0-85 35t-35 85q0 50 35 85t85 35h160v80ZM320-440v-80h320v80H320Zm200 160v-80h160q50 0 85-35t35-85q0-50-35-85t-85-35H520v-80h160q83 0 141.5 58.5T880-480q0 83-58.5 141.5T680-280H520Z" })
  );
};

// src/icons/material-symbols/outlined/LiveHelp/LiveHelp.react.tsx
import React65 from "react";
var LiveHelp = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React65.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React65.createElement("title", null, "Live Help"),
    /* @__PURE__ */ React65.createElement("path", { d: "M476-280q21 0 35.5-14.5T526-330q0-21-14.5-35.5T476-380q-21 0-35.5 14.5T426-330q0 21 14.5 35.5T476-280Zm-36-154h74q0-17 1.5-29t6.5-23q5-11 12.5-20.5T556-530q35-35 49.5-58.5T620-642q0-53-36-85.5T487-760q-55 0-93.5 27T340-658l66 26q7-27 28-43.5t49-16.5q27 0 45 14.5t18 38.5q0 17-11 36t-37 42q-17 14-27.5 27.5T453-505q-7 15-10 31.5t-3 39.5Zm40 394L360-160H200q-33 0-56.5-23.5T120-240v-560q0-33 23.5-56.5T200-880h560q33 0 56.5 23.5T840-800v560q0 33-23.5 56.5T760-160H600L480-40ZM200-240h192l88 88 88-88h192v-560H200v560Zm280-280Z" })
  );
};

// src/icons/material-symbols/outlined/Login/Login.react.tsx
import React66 from "react";
var Login = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React66.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React66.createElement("title", null, "Login"),
    /* @__PURE__ */ React66.createElement("path", { d: "M480-120v-80h280v-560H480v-80h280q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H480Zm-80-160-55-58 102-102H120v-80h327L345-622l55-58 200 200-200 200Z" })
  );
};

// src/icons/material-symbols/outlined/Logout/Logout.react.tsx
import React67 from "react";
var Logout = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React67.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React67.createElement("title", null, "Logout"),
    /* @__PURE__ */ React67.createElement("path", { d: "M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h280v80H200Zm440-160-55-58 102-102H360v-80h327L585-622l55-58 200 200-200 200Z" })
  );
};

// src/icons/material-symbols/outlined/Menu.react.tsx
import React68 from "react";
var Menu = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React68.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React68.createElement("title", null, "Menu"),
    /* @__PURE__ */ React68.createElement("path", { d: "M120-240v-80h720v80H120Zm0-200v-80h720v80H120Zm0-200v-80h720v80H120Z" })
  );
};

// src/icons/material-symbols/outlined/MoreHoriz.react.tsx
import React69 from "react";
var MoreHoriz = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React69.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React69.createElement("title", null, "More horiz"),
    /* @__PURE__ */ React69.createElement("path", { d: "M240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400Z" })
  );
};

// src/icons/material-symbols/outlined/NaturePeople.react.tsx
import React70 from "react";
var NaturePeople = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React70.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React70.createElement("title", null, "Nature people"),
    /* @__PURE__ */ React70.createElement("path", { d: "M180-520q-26 0-43-17t-17-43q0-26 17-43t43-17q26 0 43 17t17 43q0 26-17 43t-43 17ZM120-80v-200H80v-160q0-17 11.5-28.5T120-480h120q17 0 28.5 11.5T280-440v160h-40v120h320v-200h-70q-71 0-120.5-49.5T320-530q0-53 28.5-94.5T422-686q11-65 60.5-109.5T600-840q68 0 117.5 44.5T778-686q45 20 73.5 61.5T880-530q0 71-49.5 120.5T710-360h-70v200h200v80H120Zm370-360h220q38 0 64-26t26-64q0-27-14.5-49T746-612l-42-18-6-44q-6-37-33.5-61.5T600-760q-37 0-64.5 24.5T502-674l-6 44-42 18q-25 11-39.5 33T400-530q0 38 26 64t64 26Zm110-160Z" })
  );
};

// src/icons/material-symbols/outlined/Notifications.react.tsx
import React71 from "react";
var Notifications = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React71.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React71.createElement("title", null, "Notifications"),
    /* @__PURE__ */ React71.createElement("path", { d: "M160-200v-80h80v-280q0-83 50-147.5T420-792v-28q0-25 17.5-42.5T480-880q25 0 42.5 17.5T540-820v28q80 20 130 84.5T720-560v280h80v80H160Zm320-300Zm0 420q-33 0-56.5-23.5T400-160h160q0 33-23.5 56.5T480-80ZM320-280h320v-280q0-66-47-113t-113-47q-66 0-113 47t-47 113v280Z" })
  );
};

// src/icons/material-symbols/outlined/NotificationsFilled/NotificationsFilled.react.tsx
import React72 from "react";
var NotificationsFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React72.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React72.createElement("title", null, "Notifications"),
    /* @__PURE__ */ React72.createElement("path", { d: "M160-200v-80h80v-280q0-83 50-147.5T420-792v-28q0-25 17.5-42.5T480-880q25 0 42.5 17.5T540-820v28q80 20 130 84.5T720-560v280h80v80H160ZM480-80q-33 0-56.5-23.5T400-160h160q0 33-23.5 56.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/OpenInNew.react.tsx
import React73 from "react";
var OpenInNew = ({
  size = 24,
  className,
  fill: fill2 = "current",
  title = "Open In New Tab",
  ...rest
}) => {
  return /* @__PURE__ */ React73.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React73.createElement("title", null, title),
    /* @__PURE__ */ React73.createElement("path", { d: "M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h560v-280h80v280q0 33-23.5 56.5T760-120H200Zm188-212-56-56 372-372H560v-80h280v280h-80v-144L388-332Z" })
  );
};

// src/icons/material-symbols/outlined/Paid.react.tsx
import React74 from "react";
var Paid = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React74.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React74.createElement("title", null, "Paid"),
    /* @__PURE__ */ React74.createElement("path", { d: "M444-200h70v-50q50-9 86-39t36-89q0-42-24-77t-96-61q-60-20-83-35t-23-41q0-26 18.5-41t53.5-15q32 0 50 15.5t26 38.5l64-26q-11-35-40.5-61T516-710v-50h-70v50q-50 11-78 44t-28 74q0 47 27.5 76t86.5 50q63 23 87.5 41t24.5 47q0 33-23.5 48.5T486-314q-33 0-58.5-20.5T390-396l-66 26q14 48 43.5 77.5T444-252v52Zm36 120q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" })
  );
};

// src/icons/material-symbols/outlined/PaidFilled.react.tsx
import React75 from "react";
var PaidFilled = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React75.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React75.createElement("title", null, "Paid"),
    /* @__PURE__ */ React75.createElement("path", { d: "M444-200h70v-50q50-9 86-39t36-89q0-42-24-77t-96-61q-60-20-83-35t-23-41q0-26 18.5-41t53.5-15q32 0 50 15.5t26 38.5l64-26q-11-35-40.5-61T516-710v-50h-70v50q-50 11-78 44t-28 74q0 47 27.5 76t86.5 50q63 23 87.5 41t24.5 47q0 33-23.5 48.5T486-314q-33 0-58.5-20.5T390-396l-66 26q14 48 43.5 77.5T444-252v52Zm36 120q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/Password.react.tsx
import React76 from "react";
var Password = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React76.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React76.createElement("title", null, "Password"),
    /* @__PURE__ */ React76.createElement("path", { d: "M80-200v-80h800v80H80Zm46-242-52-30 34-60H40v-60h68l-34-58 52-30 34 58 34-58 52 30-34 58h68v60h-68l34 60-52 30-34-60-34 60Zm320 0-52-30 34-60h-68v-60h68l-34-58 52-30 34 58 34-58 52 30-34 58h68v60h-68l34 60-52 30-34-60-34 60Zm320 0-52-30 34-60h-68v-60h68l-34-58 52-30 34 58 34-58 52 30-34 58h68v60h-68l34 60-52 30-34-60-34 60Z" })
  );
};

// src/icons/material-symbols/outlined/Person.react.tsx
import React77 from "react";
var Person = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React77.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React77.createElement("title", null, "Person"),
    /* @__PURE__ */ React77.createElement("path", { d: "M480-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM160-160v-112q0-34 17.5-62.5T224-378q62-31 126-46.5T480-440q66 0 130 15.5T736-378q29 15 46.5 43.5T800-272v112H160Zm80-80h480v-32q0-11-5.5-20T700-306q-54-27-109-40.5T480-360q-56 0-111 13.5T260-306q-9 5-14.5 14t-5.5 20v32Zm240-320q33 0 56.5-23.5T560-640q0-33-23.5-56.5T480-720q-33 0-56.5 23.5T400-640q0 33 23.5 56.5T480-560Zm0-80Zm0 400Z" })
  );
};

// src/icons/material-symbols/outlined/PhoneAndroid.react.tsx
import React78 from "react";
var PhoneAndroid = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React78.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React78.createElement("title", null, "Phone Android"),
    /* @__PURE__ */ React78.createElement("path", { d: "M400-160h160v-40H400v40ZM280-40q-33 0-56.5-23.5T200-120v-720q0-33 23.5-56.5T280-920h400q33 0 56.5 23.5T760-840v720q0 33-23.5 56.5T680-40H280Zm0-200v120h400v-120H280Zm0-80h400v-400H280v400Zm0-480h400v-40H280v40Zm0 560v120-120Zm0-560v-40 40Z" })
  );
};

// src/icons/material-symbols/outlined/Repeat.react.tsx
import React79 from "react";
var Repeat = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React79.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React79.createElement("title", null, "Repeat"),
    /* @__PURE__ */ React79.createElement("path", { d: "M280-80 120-240l160-160 56 58-62 62h406v-160h80v240H274l62 62-56 58Zm-80-440v-240h486l-62-62 56-58 160 160-160 160-56-58 62-62H280v160h-80Z" })
  );
};

// src/icons/material-symbols/outlined/Satellite.react.tsx
import React80 from "react";
var Satellite = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React80.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React80.createElement("title", null, "Satellite"),
    /* @__PURE__ */ React80.createElement("path", { d: "M240-280h480L570-480 450-320l-90-120-120 160Zm0-200q100 0 170-70t70-170h-68q0 72-50 122t-122 50v68Zm0-136q43 0 72.5-30.5T342-720H240v104Zm-40 496q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0 0v-560 560Z" })
  );
};

// src/icons/material-symbols/outlined/Search.react.tsx
import React81 from "react";
var Search = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React81.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "imt",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React81.createElement("title", null, "Search"),
    /* @__PURE__ */ React81.createElement("path", { d: "M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" })
  );
};

// src/icons/material-symbols/outlined/Security.react.tsx
import React82 from "react";
var Security = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React82.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React82.createElement("title", null, "Security"),
    /* @__PURE__ */ React82.createElement("path", { d: "M480-80q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Zm0-84q97-30 162-118.5T718-480H480v-315l-240 90v207q0 7 2 18h238v316Z" })
  );
};

// src/icons/material-symbols/outlined/SmartphoneFilled.react.tsx
import React83 from "react";
var SmartphoneFilled = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React83.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React83.createElement("title", null, "Smartphone"),
    /* @__PURE__ */ React83.createElement("path", { d: "M280-40q-33 0-56.5-23.5T200-120v-720q0-33 23.5-56.5T280-920h400q33 0 56.5 23.5T760-840v720q0 33-23.5 56.5T680-40H280Zm0-200h400v-480H280v480Z" })
  );
};

// src/icons/material-symbols/outlined/Send.react.tsx
import React84 from "react";
var Send = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React84.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React84.createElement("title", null, "Send"),
    /* @__PURE__ */ React84.createElement("path", { d: "M120-160v-640l760 320-760 320Zm80-120 474-200-474-200v140l240 60-240 60v140Zm0 0v-400 400Z" })
  );
};

// src/icons/material-symbols/outlined/Settings/Settings.react.tsx
import React85 from "react";
var Settings = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React85.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React85.createElement("title", null, "Settings"),
    /* @__PURE__ */ React85.createElement("path", { d: "m370-80-16-128q-13-5-24.5-12T307-235l-119 50L78-375l103-78q-1-7-1-13.5v-27q0-6.5 1-13.5L78-585l110-190 119 50q11-8 23-15t24-12l16-128h220l16 128q13 5 24.5 12t22.5 15l119-50 110 190-103 78q1 7 1 13.5v27q0 6.5-2 13.5l103 78-110 190-118-50q-11 8-23 15t-24 12L590-80H370Zm70-80h79l14-106q31-8 57.5-23.5T639-327l99 41 39-68-86-65q5-14 7-29.5t2-31.5q0-16-2-31.5t-7-29.5l86-65-39-68-99 42q-22-23-48.5-38.5T533-694l-13-106h-79l-14 106q-31 8-57.5 23.5T321-633l-99-41-39 68 86 64q-5 15-7 30t-2 32q0 16 2 31t7 30l-86 65 39 68 99-42q22 23 48.5 38.5T427-266l13 106Zm42-180q58 0 99-41t41-99q0-58-41-99t-99-41q-59 0-99.5 41T342-480q0 58 40.5 99t99.5 41Zm-2-140Z" })
  );
};

// src/icons/material-symbols/outlined/SettingsFilled.react.tsx
import React86 from "react";
var SettingsFilled = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React86.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React86.createElement("title", null, "Settings"),
    /* @__PURE__ */ React86.createElement("path", { d: "m370-80-16-128q-13-5-24.5-12T307-235l-119 50L78-375l103-78q-1-7-1-13.5v-27q0-6.5 1-13.5L78-585l110-190 119 50q11-8 23-15t24-12l16-128h220l16 128q13 5 24.5 12t22.5 15l119-50 110 190-103 78q1 7 1 13.5v27q0 6.5-2 13.5l103 78-110 190-118-50q-11 8-23 15t-24 12L590-80H370Zm112-260q58 0 99-41t41-99q0-58-41-99t-99-41q-59 0-99.5 41T342-480q0 58 40.5 99t99.5 41Z" })
  );
};

// src/icons/material-symbols/outlined/ShoppingCart.react.tsx
import React87 from "react";
var ShoppingCart = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React87.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React87.createElement("title", null, "Shopping Cart"),
    /* @__PURE__ */ React87.createElement("path", { d: "M280-80q-33 0-56.5-23.5T200-160q0-33 23.5-56.5T280-240q33 0 56.5 23.5T360-160q0 33-23.5 56.5T280-80Zm400 0q-33 0-56.5-23.5T600-160q0-33 23.5-56.5T680-240q33 0 56.5 23.5T760-160q0 33-23.5 56.5T680-80ZM246-720l96 200h280l110-200H246Zm-38-80h590q23 0 35 20.5t1 41.5L692-482q-11 20-29.5 31T622-440H324l-44 80h480v80H280q-45 0-68-39.5t-2-78.5l54-98-144-304H40v-80h130l38 80Zm134 280h280-280Z" })
  );
};

// src/icons/material-symbols/outlined/ShoppingCartFilled/ShoppingCartFilled.react.tsx
import React88 from "react";
var ShoppingCartFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React88.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React88.createElement("title", null, "Shopping Cart"),
    /* @__PURE__ */ React88.createElement("path", { d: "M280-80q-33 0-56.5-23.5T200-160q0-33 23.5-56.5T280-240q33 0 56.5 23.5T360-160q0 33-23.5 56.5T280-80Zm400 0q-33 0-56.5-23.5T600-160q0-33 23.5-56.5T680-240q33 0 56.5 23.5T760-160q0 33-23.5 56.5T680-80ZM208-800h590q23 0 35 20.5t1 41.5L692-482q-11 20-29.5 31T622-440H324l-44 80h480v80H280q-45 0-68-39.5t-2-78.5l54-98-144-304H40v-80h130l38 80Z" })
  );
};

// src/icons/material-symbols/outlined/SignalCellularAltFont300/SignalCellularAltFont300.react.tsx
import React89 from "react";
var SignalCellularAltFont300 = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Signal Cellular Alt",
  ...rest
}) => {
  return /* @__PURE__ */ React89.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React89.createElement("title", null, title),
    /* @__PURE__ */ React89.createElement("path", { d: "M220.001-180.001v-199.998h92.306v199.998h-92.306Zm236.154 0v-399.998h92.306v399.998h-92.306Zm231.538 0v-599.998h92.306v599.998h-92.306Z" })
  );
};

// src/icons/material-symbols/outlined/SignalCellularAltFont600/SignalCellularAltFont600.react.tsx
import React90 from "react";
var SignalCellularAltFont600 = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Signal Cellular Alt",
  ...rest
}) => {
  return /* @__PURE__ */ React90.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React90.createElement("title", null, title),
    /* @__PURE__ */ React90.createElement("path", { d: "M186.434-140.782v-221.912h146.001v221.912H186.434Zm243.392 0v-421.912h146.001v421.912H429.826Zm243.391 0v-678.436h146.001v678.436H673.217Z" })
  );
};

// src/icons/material-symbols/outlined/Star/Star.react.tsx
import React91 from "react";
var Star = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Star",
  ...rest
}) => {
  return /* @__PURE__ */ React91.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React91.createElement("title", null, title),
    /* @__PURE__ */ React91.createElement("path", { d: "m354-247 126-76 126 77-33-144 111-96-146-13-58-136-58 135-146 13 111 97-33 143ZM233-80l65-281L80-550l288-25 112-265 112 265 288 25-218 189 65 281-247-149L233-80Zm247-350Z" })
  );
};

// src/icons/material-symbols/outlined/StarFilled/StarFilled.react.tsx
import React92 from "react";
var StarFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Star",
  ...rest
}) => {
  return /* @__PURE__ */ React92.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React92.createElement("title", null, title),
    /* @__PURE__ */ React92.createElement("path", { d: "m233-80 65-281L80-550l288-25 112-265 112 265 288 25-218 189 65 281-247-149L233-80Z" })
  );
};

// src/icons/material-symbols/outlined/StylusNote.react.tsx
import React93 from "react";
var StylusNote = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React93.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React93.createElement("title", null, "Stylus Note"),
    /* @__PURE__ */ React93.createElement("path", { d: "m499-287 335-335-52-52-335 335 52 52Zm-261 87q-100-5-149-42T40-349q0-65 53.5-105.5T242-503q39-3 58.5-12.5T320-542q0-26-29.5-39T193-600l7-80q103 8 151.5 41.5T400-542q0 53-38.5 83T248-423q-64 5-96 23.5T120-349q0 35 28 50.5t94 18.5l-4 80Zm280 7L353-358l382-382q20-20 47.5-20t47.5 20l70 70q20 20 20 47.5T900-575L518-193Zm-159 33q-17 4-30-9t-9-30l33-159 165 165-159 33Z" })
  );
};

// src/icons/material-symbols/outlined/StylusNoteFilled/StylusNoteFilled.react.tsx
import React94 from "react";
var StylusNoteFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React94.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React94.createElement("title", null, "Stylus Note"),
    /* @__PURE__ */ React94.createElement("path", { d: "M238-200q-100-5-149-42T40-349q0-65 53.5-105.5T242-503q39-3 58.5-12.5T320-542q0-26-29.5-39T193-600l7-80q103 8 151.5 41.5T400-542q0 53-38.5 83T248-423q-64 5-96 23.5T120-349q0 35 28 50.5t94 18.5l-4 80Zm317-30L390-395l345-345q20-20 47.5-20t47.5 20l70 70q20 20 20 47.5T900-575L555-230Zm-196 70q-17 4-30-9t-9-30l31-151 158 158-150 32Z" })
  );
};

// src/icons/material-symbols/outlined/SwapHoriz.react.tsx
import React95 from "react";
var SwapHoriz = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React95.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React95.createElement("title", null, "Swap"),
    /* @__PURE__ */ React95.createElement("path", { d: "M280-160 80-360l200-200 56 57-103 103h287v80H233l103 103-56 57Zm400-240-56-57 103-103H440v-80h287L624-743l56-57 200 200-200 200Z" })
  );
};

// src/icons/material-symbols/outlined/Translate/Translate.react.tsx
import React96 from "react";
var Translate = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React96.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React96.createElement("title", null, "Translate"),
    /* @__PURE__ */ React96.createElement("path", { d: "m476-80 182-480h84L924-80h-84l-43-122H603L560-80h-84ZM160-200l-56-56 202-202q-35-35-63.5-80T190-640h84q20 39 40 68t48 58q33-33 68.5-92.5T484-720H40v-80h280v-80h80v80h280v80H564q-21 72-63 148t-83 116l96 98-30 82-122-125-202 201Zm468-72h144l-72-204-72 204Z" })
  );
};

// src/icons/material-symbols/outlined/Verified/Verified.react.tsx
import React97 from "react";
var Verified = ({
  size = 24,
  fill: fill2 = "blue-3",
  innerFill = "white",
  className,
  title = "Verified",
  ...rest
}) => {
  return /* @__PURE__ */ React97.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React97.createElement("title", null, title),
    /* @__PURE__ */ React97.createElement("path", { d: "m344-60-76-128-144-32 14-148-98-112 98-112-14-148 144-32 76-128 136 58 136-58 76 128 144 32-14 148 98 112-98 112 14 148-144 32-76 128-136-58-136 58Zm94-278 226-226-56-58-170 170-86-84-56 56 142 142Z" }),
    /* @__PURE__ */ React97.createElement(
      "path",
      {
        className: fillVariants({ fill: innerFill }),
        d: "M438-338 L664-564 L608-622 L438-452 L352-538 L296-482 L438-338 Z"
      }
    )
  );
};

// src/icons/material-symbols/outlined/VerifiedUser.react.tsx
import React98 from "react";
var VerifiedUser = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React98.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React98.createElement("title", null, "Verified User"),
    /* @__PURE__ */ React98.createElement("path", { d: "m438-338 226-226-57-57-169 169-84-84-57 57 141 141Zm42 258q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Zm0-84q104-33 172-132t68-220v-189l-240-90-240 90v189q0 121 68 220t172 132Zm0-316Z" })
  );
};

// src/icons/material-symbols/outlined/ViewList.react.tsx
import React99 from "react";
var ViewList = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React99.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React99.createElement("title", null, "View List"),
    /* @__PURE__ */ React99.createElement("path", { d: "M360-240h440v-107H360v107ZM160-613h120v-107H160v107Zm0 187h120v-107H160v107Zm0 186h120v-107H160v107Zm200-186h440v-107H360v107Zm0-187h440v-107H360v107ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Z" })
  );
};

// src/icons/material-symbols/outlined/ViewListFilled.react.tsx
import React100 from "react";
var ViewListFilled = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React100.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React100.createElement("title", null, "View List"),
    /* @__PURE__ */ React100.createElement("path", { d: "M360-160h440q33 0 56.5-23.5T880-240v-80H360v160ZM80-640h200v-160H160q-33 0-56.5 23.5T80-720v80Zm0 240h200v-160H80v160Zm80 240h120v-160H80v80q0 33 23.5 56.5T160-160Zm200-240h520v-160H360v160Zm0-240h520v-80q0-33-23.5-56.5T800-800H360v160Z" })
  );
};

// src/icons/material-symbols/outlined/VpnKey.react.tsx
import React101 from "react";
var VpnKey = ({
  size = 24,
  className,
  fill: fill2 = "current",
  ...rest
}) => {
  return /* @__PURE__ */ React101.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React101.createElement("title", null, "VPN Key"),
    /* @__PURE__ */ React101.createElement("path", { d: "M280-240q-100 0-170-70T40-480q0-100 70-170t170-70q66 0 121 33t87 87h432v240h-80v120H600v-120H488q-32 54-87 87t-121 33Zm0-80q66 0 106-40.5t48-79.5h246v120h80v-120h80v-80H434q-8-39-48-79.5T280-640q-66 0-113 47t-47 113q0 66 47 113t113 47Zm0-80q33 0 56.5-23.5T360-480q0-33-23.5-56.5T280-560q-33 0-56.5 23.5T200-480q0 33 23.5 56.5T280-400Zm0-80Z" })
  );
};

// src/icons/material-symbols/outlined/Visibility.react.tsx
import React102 from "react";
var Visibility = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React102.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React102.createElement("title", null, "Visibility"),
    /* @__PURE__ */ React102.createElement("path", { d: "M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392Zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200Zm0-300Zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280Z" })
  );
};

// src/icons/material-symbols/outlined/Wallet.react.tsx
import React103 from "react";
var Wallet = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React103.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest,
      role: "img"
    },
    /* @__PURE__ */ React103.createElement("title", null, "Wallet"),
    /* @__PURE__ */ React103.createElement("path", { d: "M240-160q-66 0-113-47T80-320v-320q0-66 47-113t113-47h480q66 0 113 47t47 113v320q0 66-47 113t-113 47H240Zm0-480h480q22 0 42 5t38 16v-21q0-33-23.5-56.5T720-720H240q-33 0-56.5 23.5T160-640v21q18-11 38-16t42-5Zm-74 130 445 108q9 2 18 0t17-8l139-116q-11-15-28-24.5t-37-9.5H240q-26 0-45.5 13.5T166-510Z" })
  );
};

// src/icons/material-symbols/outlined/WarningFilled.react.tsx
import React104 from "react";
var WarningFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React104.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React104.createElement("title", null, "Warning"),
    /* @__PURE__ */ React104.createElement("path", { d: "m40-120 440-760 440 760H40Zm440-120q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm-40-120h80v-200h-80v200Z" })
  );
};

// src/icons/material-symbols/outlined/Window/Window.react.tsx
import React105 from "react";
var Window = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React105.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React105.createElement("title", null, "Window"),
    /* @__PURE__ */ React105.createElement("path", { d: "M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm320-320v240h240v-240H520Zm0-80h240v-240H520v240Zm-80 0v-240H200v240h240Zm0 80H200v240h240v-240Z" })
  );
};

// src/icons/material-symbols/outlined/YourTrips.react.tsx
import React106 from "react";
var YourTrips = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React106.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React106.createElement("title", null, "Your trips"),
    /* @__PURE__ */ React106.createElement("path", { d: "M320-240h320v-80H320v80ZM280-80q-33 0-56.5-23.5T200-160v-320q0-85 44.5-152T360-732v-28q0-50 35-85t85-35q50 0 85 35t35 85v28q71 33 115.5 100T760-480v320q0 33-23.5 56.5T680-80H280Zm0-80h400v-320q0-83-58.5-141.5T480-680q-83 0-141.5 58.5T280-480v320Zm160-596q11-2 20-3t20-1q11 0 20 1t20 3v-4q0-17-11.5-28.5T480-800q-17 0-28.5 11.5T440-760v4ZM280-160h400-400Zm126-220 74-56 74 56-28-91 74-53h-91l-29-96-29 96h-91l74 53-28 91Z" })
  );
};

// src/icons/material-symbols/outlined/YoutubeActivity.react.tsx
import React107 from "react";
var YoutubeActivity = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React107.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React107.createElement("title", null, "Youtube activity"),
    /* @__PURE__ */ React107.createElement("path", { d: "M616-242q-27 1-51.5 1.5t-43.5.5h-41q-71 0-133-2-53-2-104.5-5.5T168-257q-26-7-45-26t-26-45q-6-23-9.5-56T82-447q-2-36-2-73t2-73q2-30 5.5-63t9.5-56q7-26 26-45t45-26q23-6 74.5-9.5T347-798q62-2 133-2t133 2q53 2 104.5 5.5T792-783q26 7 45 26t26 45q6 23 9.5 56t5.5 63q2 36 2 73v17q-19-8-39-12.5t-41-4.5q-83 0-141.5 58.5T600-320q0 21 4 40.5t12 37.5ZM400-400l208-120-208-120v240Zm360 200v-80h-80v-80h80v-80h80v80h80v80h-80v80h-80Z" })
  );
};

// src/icons/material-symbols/outlined/ZoomIn.react.tsx
import React108 from "react";
var ZoomIn = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React108.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React108.createElement("title", null, "Zoom in"),
    /* @__PURE__ */ React108.createElement("path", { d: "M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Zm-40-60v-80h-80v-80h80v-80h80v80h80v80h-80v80h-80Z" })
  );
};

// src/icons/material-symbols/outlined/ZoomOut.react.tsx
import React109 from "react";
var ZoomOut = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React109.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React109.createElement("title", null, "Zoom out"),
    /* @__PURE__ */ React109.createElement("path", { d: "M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400ZM280-540v-80h200v80H280Z" })
  );
};

// src/icons/social/Twitter/Twitter.react.tsx
import React110 from "react";
var Twitter = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React110.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      fill: "none",
      height: size,
      viewBox: "0 0 24 24",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React110.createElement("g", { id: "Twitter" }, /* @__PURE__ */ React110.createElement(
      "path",
      {
        d: "M22 5.83185C21.2523 6.14338 20.5047 6.3926 19.6324 6.45491C20.5047 5.95647 21.1277 5.1465 21.4393 4.21192C20.6293 4.71036 19.757 5.02189 18.8224 5.2088C18.0748 4.39883 17.0156 3.90039 15.8941 3.90039C13.6511 3.90039 11.7819 5.70724 11.7819 8.01254C11.7819 8.32407 11.8442 8.63559 11.9065 8.94712C8.47975 8.7602 5.4891 7.14027 3.43302 4.64805C3.05919 5.27111 2.87227 5.95647 2.87227 6.70413C2.87227 8.13715 3.61994 9.38326 4.67913 10.1309C3.99377 10.1309 3.37072 9.944 2.80997 9.63248V9.69478C2.80997 11.6886 4.24299 13.3085 6.11215 13.6823C5.73832 13.7446 5.42679 13.8069 5.05296 13.8069C4.80374 13.8069 4.55452 13.8069 4.3053 13.7446C4.80374 15.3646 6.36137 16.5484 8.10592 16.6107C6.7352 17.7322 4.92835 18.3552 2.99688 18.3552C2.68536 18.3552 2.31153 18.3552 2 18.2929C3.86916 19.4144 6.04985 20.0998 8.35514 20.0998C15.8941 20.0998 20.0062 13.8692 20.0062 8.44868C20.0062 8.26176 20.0062 8.07485 20.0062 7.95023C20.7539 7.32718 21.4393 6.64182 22 5.83185Z",
        id: "Vector"
      }
    ))
  );
};

// src/icons/marketplaces/OpenseaLogo/OpenseaLogo.react.tsx
import React111 from "react";
var OpenseaLogo = ({
  size = 24,
  ...rest
}) => /* @__PURE__ */ React111.createElement(
  "svg",
  {
    fill: "none",
    height: size,
    viewBox: "0 0 30 30",
    width: size,
    xmlns: "http://www.w3.org/2000/svg",
    ...rest
  },
  /* @__PURE__ */ React111.createElement("g", { clipPath: "url(#clip0_2579_1444)" }, /* @__PURE__ */ React111.createElement("circle", { cx: "15", cy: "15", fill: "url(#paint0_linear_2579_1444)", r: "15" }), /* @__PURE__ */ React111.createElement(
    "path",
    {
      d: "M10.3336 7.25287C11.3107 8.49466 11.896 10.0647 11.896 11.7696C11.896 13.2455 11.4583 14.6196 10.7051 15.7673H5.41223L10.3336 7.25287Z",
      fill: "white"
    }
  ), /* @__PURE__ */ React111.createElement(
    "path",
    {
      d: "M26.4718 17.6518C26.4718 17.7231 26.4336 17.7841 26.3675 17.8121C26.0087 17.9648 24.8254 18.5145 24.3317 19.199C23.062 20.9675 22.0924 23.7463 19.9244 23.7463H10.8807C7.67698 23.7488 4.99746 21.2067 5 17.8198C5 17.7358 5.07125 17.6671 5.15523 17.6671H9.44297C9.59056 17.6671 9.70761 17.7867 9.70761 17.9317V18.7587C9.70761 19.199 10.0639 19.5552 10.5041 19.5552H13.7562V17.662H11.5347C12.8121 16.0436 13.5729 14.0002 13.5729 11.7762C13.5729 9.29518 12.6212 7.03298 11.0639 5.34079C12.0054 5.45021 12.9062 5.63851 13.7562 5.88789V5.36115C13.7562 4.81405 14.1989 4.37128 14.746 4.37128C15.2931 4.37128 15.7359 4.81405 15.7359 5.36115V6.63093C18.7717 8.0483 20.759 10.3996 20.759 13.0587C20.759 14.6186 20.0771 16.0716 18.8989 17.293C18.6724 17.5271 18.3594 17.6595 18.0312 17.6595H15.7359V19.5501H18.6164C19.2373 19.5501 20.3519 18.372 20.8812 17.662C20.8812 17.662 20.9041 17.6264 20.9652 17.6086C21.0262 17.5908 26.2555 16.3897 26.2555 16.3897C26.3649 16.3591 26.4743 16.4431 26.4743 16.5551V17.6416L26.4718 17.6518Z",
      fill: "white"
    }
  )),
  /* @__PURE__ */ React111.createElement("defs", null, /* @__PURE__ */ React111.createElement(
    "linearGradient",
    {
      gradientUnits: "userSpaceOnUse",
      id: "paint0_linear_2579_1444",
      x1: "15",
      x2: "15",
      y1: "0",
      y2: "30"
    },
    /* @__PURE__ */ React111.createElement("stop", { stopColor: "#21AFFF" }),
    /* @__PURE__ */ React111.createElement("stop", { offset: "1", stopColor: "#007EFF" })
  ), /* @__PURE__ */ React111.createElement("clipPath", { id: "clip0_2579_1444" }, /* @__PURE__ */ React111.createElement("rect", { fill: "white", height: "30", rx: "15", width: "30" })))
);

// src/icons/marketplaces/BlurLogo.react.tsx
import React112 from "react";
var BlurLogo = ({ size = 24, ...rest }) => {
  return /* @__PURE__ */ React112.createElement(
    "svg",
    {
      fill: "none",
      height: size,
      role: "img",
      viewBox: "0 0 24 24",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React112.createElement("title", null, "Blur Logo"),
    /* @__PURE__ */ React112.createElement("circle", { cx: "12", cy: "12", fill: "#080404", r: "12" }),
    /* @__PURE__ */ React112.createElement("path", { d: "M6 14.6406V15.5345H7.42858V14.6406H6Z", fill: "#FF8700" }),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M10.1428 14.6406V15.5345H11.5714V14.6406H10.1428Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement("path", { d: "M7.42858 15.832H6V16.5769H7.42858V15.832Z", fill: "#FF8700" }),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M13.857 8.68138V7.93652H12.4285V8.68138H13.857Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M7.49854 11.7995V7.125H9.94426L10.2128 7.42294V7.99596H11.5714V7.125L11.2357 6.74512H6.14282V11.7995H7.49854Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M13.857 8.97949H12.4285V9.87332H13.857V8.97949Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M10.1428 15.832V16.5769H11.5714V15.832H10.1428Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M16.6085 14.6406V14.7628L16.3228 15.0712H13.8228V14.6406H12.4285V15.5345L17.2231 15.8978L17.9999 15.0712V14.6406H16.6085Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M12.4285 13.6572V14.3425H13.8228V13.9135H16.3285L16.6142 14.1682V14.9065H17.9999V13.9135L17.6599 13.5977H12.4285V13.6572Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M10.9375 8.89739L11.5714 8.29555V7.93652H10.2128V8.03931L9.94426 8.29555H7.49854V7.93652H6.14282V8.94204L10.9375 8.89739Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M17.991 13.8979L16.9128 12.7041H12.4285V13.6836L17.991 13.8979Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M12.4285 16.2316V16.5772H13.8228V16.2316H16.3285L16.6142 16.4878V18.5138H17.9999V16.2316L17.2678 15.5763L12.4285 15.4424V16.2316Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M7.49854 9.87263V9.45699H9.94426L10.2128 9.76537V9.87263H11.5714V9.45699L10.8303 8.62012L6.14282 8.9788V9.87263H7.49854Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M11.2543 10.916L11.5714 10.6181V9.83496H10.2128V10.3559L9.94426 10.6181H7.49854V10.1712H6.14282V10.916H11.2543Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M10.1428 13.5977V14.3425H11.5714V13.5977H10.1428Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M6.14282 10.6925V11.8102H10.4857L11.5714 10.6211L6.14282 10.6925Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M12.4463 10.6113L13.527 11.8094H17.4284V10.6113H12.4463Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement("path", { d: "M13.857 6H12.4285V10.5136H13.857V6Z", fill: "#FF8700" }),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M11.5714 7.11182L10.5446 6H6.14282V7.11182H11.5714Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M6.57153 18.0664L7.10296 18.5133H10.4687L11.0001 18.0664H6.57153Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M13.857 12.96H12.4285V18.5135H13.857V12.96Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M6 13.9135V14.3425H7.42858V13.5977H6V13.9135Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M17.4285 10.618H14.3728L13.6785 9.96875L12.4285 10.171V10.618L12.757 10.9159H17.4285V10.618Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M12.4285 7.125V7.63895H13.857V6.74512H12.4285V7.125Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M11.5714 17.4051V16.8747L10.2143 16.7188L9.62144 17.4051H7.95001L7.25893 16.6562L6 16.8747V17.4051L6.94643 18.5223H10.6518L11.5714 17.4051Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M11.5714 12.7041H10.1428V16.9782H11.5714V12.7041Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M13.857 16.875H12.4285V17.7688H13.857V16.875Z",
        fill: "#FF8700"
      }
    ),
    /* @__PURE__ */ React112.createElement(
      "path",
      {
        d: "M7.42858 12.7041H6V16.9782H7.42858V12.7041Z",
        fill: "#FF8700"
      }
    )
  );
};
export {
  AccountCircle,
  Add,
  Apps,
  AppsFont600,
  ArbitrumLogo,
  ArrowBack,
  ArrowDownward,
  ArrowDropDownFilled,
  ArrowDropUpFilled,
  ArrowForward,
  ArrowLeft,
  ArrowRight,
  Autorenew,
  AvalancheLogo,
  AwardStar,
  BaseLogo,
  BinanceLogo,
  BlurLogo,
  Bolt,
  Calculate,
  CalendarAddOn,
  CalendarToday,
  CalendarTodayFilled,
  CancelFilled,
  Check,
  CheckCircleFilled,
  ChevronLeft,
  ChevronRight,
  CircleFilled,
  Close,
  DarkMode,
  Delete,
  DoNotDisturbOn,
  Edit,
  EmojiPeople,
  Error,
  ErrorFilled,
  EthereumLogo,
  ExpandAll,
  ExpandLess,
  ExpandMore,
  Explore,
  ExploreFilled,
  Favorite,
  FeatureSearch,
  FeaturedVideo,
  FilterList,
  Flag,
  FormatListBulleted,
  Grid3x3,
  Home,
  HomeFilled,
  ImportContacts,
  InfoFilled,
  KlaytnLogo,
  Language,
  Leaderboard,
  LeaderboardFilled,
  LightMode,
  Link,
  LiveHelp,
  Login,
  Logout,
  Menu,
  MoreHoriz,
  NaturePeople,
  Notifications,
  NotificationsFilled,
  OpenInNew,
  OpenseaLogo,
  OptimismLogo,
  Paid,
  PaidFilled,
  Password,
  Person,
  PhoneAndroid,
  PolygonLogo,
  Repeat,
  Satellite,
  Search,
  Security,
  Send,
  Settings,
  SettingsFilled,
  ShoppingCart,
  ShoppingCartFilled,
  SignalCellularAltFont300,
  SignalCellularAltFont600,
  SmartphoneFilled,
  SolanaLogo,
  Star,
  StarFilled,
  StylusNote,
  StylusNoteFilled,
  SwapHoriz,
  ThreeSixty,
  Translate,
  Twitter,
  Verified,
  VerifiedUser,
  ViewList,
  ViewListFilled,
  Visibility,
  VpnKey,
  Wallet,
  WarningFilled,
  Window,
  YourTrips,
  YoutubeActivity,
  ZoomIn,
  ZoomOut,
  ZoraLogo
};
//# sourceMappingURL=index.mjs.map